import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientInformationForm } from '@/shared/forms/ClientInformationForm/ClientInformationForm.tsx';
import { ClientInformationFormValues } from '@/shared/forms/ClientInformationForm/schemas/getClientInformationFormSchema.ts';
import { useOrderContext } from '@/modules/order/context/OrderContext.tsx';
import { useSuspenseConstructionProject } from '@/clients/constructionProject/useConstructionProject.ts';
import { useOrganizationById } from '@/clients/organization/useOrganization.ts';
import { CustomerInfo } from '@/shared/models/ContextDataModel/interfaces/entities/customer-info.ts';
import { useUserSuspenseQuery } from '@/clients/users/useUsers.ts';

type ClientInformationOrderProps = {
    onContinue: () => void;
};

export const ClientInformationOrder = ({ onContinue }: ClientInformationOrderProps) => {
    const { t } = useTranslation();
    const { contextData, isPositionOrder } = useOrderContext();
    const customerInfo = contextData.getCustomerInformation();

    const handleSubmit = useCallback(
        (values: ClientInformationFormValues) => {
            contextData.setCustomerInformation({
                constructionProjectId: values.constructionProjectId,
                organizationId: values.organizationId,
                userId: values.userId,
                projectNumber: values.projectNumber,
                manualCostCenter: values.manualCostCenter,
            });
            onContinue();
        },
        [contextData, onContinue]
    );

    if (customerInfo) {
        return <ClientInformationOrderWithPrefill customerInfo={customerInfo} onContinue={onContinue} />;
    }

    return (
        <ClientInformationForm
            defaultValueLabels={{
                sourceCostCenter: t('sections.clientInformation.costCenter.sourceLabelOrder'),
                manualCostCenter: t('sections.clientInformation.costCenter.inputLabelOrder'),
            }}
            formRules={{
                organization: { disabled: isPositionOrder },
                constructionProject: { disabled: isPositionOrder },
            }}
            onSubmit={handleSubmit}
        />
    );
};

const ClientInformationOrderWithPrefill = ({
    customerInfo,
    onContinue,
}: {
    customerInfo: CustomerInfo;
    onContinue: () => void;
}) => {
    const { t } = useTranslation();
    const { contextData, isPositionOrder, position } = useOrderContext();

    const { data: constructionProject } = useSuspenseConstructionProject(customerInfo.constructionProjectId);
    const { data: organization } = useOrganizationById(customerInfo.organizationId);
    const { data: user } = useUserSuspenseQuery(customerInfo.userId);

    const handleSubmit = useCallback(
        (values: ClientInformationFormValues) => {
            const hasChangedOrganization = customerInfo.organizationId !== values.organizationId;
            const hasChangedProject = customerInfo.constructionProjectId !== values.constructionProjectId;
            if (hasChangedOrganization || hasChangedProject) {
                contextData.setBusinessCase(undefined);
            }

            contextData.setCustomerInformation({
                constructionProjectId: values.constructionProjectId,
                organizationId: values.organizationId,
                userId: values.userId,
                projectNumber: values.projectNumber,
                manualCostCenter: values.manualCostCenter,
            });
            onContinue();
        },
        [contextData, customerInfo, onContinue]
    );

    return (
        <ClientInformationForm
            defaultValues={{
                ...customerInfo,
                sourceCostCenter: position?.customer.manualCostCenter ?? constructionProject.costCenter,
            }}
            defaultValueLabels={{
                organization: organization.name,
                constructionProject: constructionProject.name,
                user: `${user.firstName} ${user.lastName}`,
                sourceCostCenter: t('sections.clientInformation.costCenter.sourceLabelOrder'),
                manualCostCenter: t('sections.clientInformation.costCenter.inputLabelOrder'),
            }}
            formRules={{
                organization: { disabled: isPositionOrder },
                constructionProject: { disabled: isPositionOrder },
                costCenter: { required: organization.costcenterMandatory },
            }}
            onSubmit={handleSubmit}
        />
    );
};
